import { BillingProcess, PlanTypes } from '@gonfalon/billing/types';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { enableSelfServeObservability } from '@gonfalon/dogfood-flags/dogfood-flags';

import type { Plan } from '../../../plan/types';
import { CheckoutProcess } from '../../../plan/types';

import { foundationPlan } from './foundationPlan';
import { aiRunsFeature } from './products';

export const developerPlan: Plan = {
  id: `${PlanTypes.FOUNDATION2023}-developer`,
  planType: PlanTypes.DEVELOPER,
  billingProcess: BillingProcess.USAGE_BASED_BILLING,
  checkoutProcess: CheckoutProcess.DIRECT_CHECKOUT,
  hasConfigurableLimits: false, // Developer is a fixed plan with no configurable limits
  isFoundationPlan: true, // Legacy Developer plan shows payment card UI (same as Foundation2023)
  requiresPaymentMethod: true,
  hasBillableProducts: true,
  isDeveloperSignup: true, // Track as developer signup for analytics
  isLegacyDeveloperFacade: true, // This is the legacy Developer plan facade (displays as "Developer / Foundation")
  recommended: true,
  useObservabilityFlagForVersion: true, // Check enableSelfServeObservability flag for version selection
  marketingCopy: {
    name: 'Developer',
    tagLine: 'A feature-rich starter plan with essential tools for builders',
    pricingHeadline: 'Free',
    pricingDescription: 'Pay-as-you-go only when you exceed your monthly allowance',
    featuresHeader: 'Developer plan includes',
    features: [
      { text: '1 project, 3 environments' },
      { text: 'Unlimited feature flags' },
      {
        text: '5 service connections per month',
        tooltip: {
          text: '1 server-side SDK connected to a LaunchDarkly environment for one month',
          ctaTarget: getDocumentationUrl('home/account/service-connections'),
        },
      },
      {
        text: '1K client-side MAU',
        tooltip: {
          text: 'Each client-side user or device creates a client-side MAU',
          ctaTarget: getDocumentationUrl('home/account/calculating-billing'),
        },
      },
      aiRunsFeature,
      {
        text: '100K experimentation MAU',
        tooltip: {
          text: 'Monthly number of users available to be targeted in Experiments',
        },
      },
      { text: 'Stale flag detection' },
      {
        text: '5K sessions',
        tooltip: {
          text: '1 recorded user session (a single instance of a tab) captured and stored for one month',
          ctaTarget: getDocumentationUrl('home/observability/session-replay'),
        },
        gatedByFlag: enableSelfServeObservability,
      },
      {
        text: '5K errors',
        tooltip: {
          text: '1 unique instance of an error or exception captured and stored for one month',
          ctaTarget: getDocumentationUrl('home/observability/errors'),
        },
        gatedByFlag: enableSelfServeObservability,
      },
      {
        text: '10M traces',
        tooltip: {
          text: 'A single span ingested, stored and searchable for one month',
          ctaTarget: getDocumentationUrl('home/observability/traces'),
        },
        gatedByFlag: enableSelfServeObservability,
      },
      {
        text: '10M logs',
        tooltip: {
          text: 'A single log line ingested, stored and searchable for one month',
          ctaTarget: getDocumentationUrl('home/observability/logs'),
        },
        gatedByFlag: enableSelfServeObservability,
      },
    ],
  },
  versions: foundationPlan.versions,
};
